'use client'

import * as React from 'react'
import { Search, FileText, Image, Database, BarChart3, CheckCircle, Clock, Eye, Download, MoreHorizontal } from 'lucide-react'
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from 'components/ui/card'
import { Button } from 'components/ui/button'
import { Input } from 'components/ui/input'
import { Badge } from 'components/ui/badge'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from 'components/ui/select'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from 'components/ui/table'
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from 'components/ui/dropdown-menu'
import { cn } from 'lib/utils'
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer } from 'recharts'
import { collection, doc, getDocs, serverTimestamp, updateDoc } from 'firebase/firestore'
import { db } from '../../../firebase'
import { toast } from 'sonner'
import { useUser } from 'components/context/user-context'

const typeIcons= {
  document: <FileText className="size-4 text-primary" />,
  photo: <Image className="size-4 text-success" />,
  survey: <BarChart3 className="size-4 text-warning" />,
  report: <FileText className="size-4 text-secondary-foreground" />,
  dataset: <Database className="size-4 text-chart-3" />,
}

function formatFileSize(bytes = 0) {
  if (bytes >= 1_000_000) return `${(bytes / 1_000_000).toFixed(1)} MB`
  if (bytes >= 1_000) return `${Math.round(bytes / 1_000)} KB`
  return `${bytes} B`
}

function formatDate(value) {
  if (!value) return '—'
  const date = typeof value?.toDate === 'function' ? value.toDate() : new Date(value)
  if (Number.isNaN(date.getTime())) return '—'
  return date.toLocaleDateString('en-KE', { month: 'short', day: 'numeric', year: 'numeric' })
}

function getEvidenceType(evidence) {
  const mime = (evidence?.type || '').toLowerCase()
  const name = (evidence?.name || '').toLowerCase()
  if (mime.startsWith('image/') || name.match(/\.(png|jpg|jpeg|gif|webp)$/)) return 'photo'
  if (mime.includes('csv') || mime.includes('spreadsheet') || mime.includes('excel') || name.endsWith('.csv')) return 'dataset'
  if (name.includes('report')) return 'report'
  if (name.includes('survey')) return 'survey'
  return 'document'
}

export default function EvidencePage() {
  const { canEdit } = useUser()
  const [search, setSearch] = React.useState('')
  const [typeFilter, setTypeFilter] = React.useState('all')
  const [verifiedFilter, setVerifiedFilter] = React.useState('all')
  const [evidenceRecords, setEvidenceRecords] = React.useState([])
  const [loading, setLoading] = React.useState(true)

  React.useEffect(() => {
    let isMounted = true

    async function loadEvidence() {
      try {
        const [indicatorSnap, projectSnap] = await Promise.all([
          getDocs(collection(db, 'indicators')),
          getDocs(collection(db, 'projects')),
        ])
        if (!isMounted) return
        const indicators = indicatorSnap.docs.map((docItem) => ({ id: docItem.id, ...docItem.data() }))
        const projects = projectSnap.docs.map((docItem) => ({ id: docItem.id, ...docItem.data() }))
        const projectMap = projects.reduce((acc, project) => {
          acc[project.id] = project
          return acc
        }, {})

        const records = indicators
          .filter((indicator) => indicator.evidence?.url)
          .map((indicator) => {
            const evidence = indicator.evidence || {}
            const type = getEvidenceType(evidence)
            const project = projectMap[indicator.projectId]
            return {
              id: indicator.id,
              title: evidence.name || indicator.name || 'Evidence',
              type,
              indicatorId: indicator.id,
              indicatorName: indicator.name || 'Indicator',
              projectName: project?.code || project?.shortCode || project?.title || indicator.projectId || 'Project',
              uploadedBy: indicator.updatedByName || indicator.createdByName || 'System',
              uploadedAt: indicator.updatedAt || indicator.createdAt,
              fileSize: evidence.size || 0,
              verified: Boolean(evidence.verified),
              url: evidence.url,
            }
          })
        setEvidenceRecords(records)
      } catch {
        if (!isMounted) return
        setEvidenceRecords([])
      } finally {
        if (isMounted) setLoading(false)
      }
    }

    loadEvidence()

    return () => {
      isMounted = false
    }
  }, [])

  const verifiedCount = evidenceRecords.filter(e => e.verified).length
  const pendingCount = evidenceRecords.filter(e => !e.verified).length
  const typePie = [
    { name: 'Documents', value: evidenceRecords.filter(e => e.type === 'document').length, color: 'oklch(0.35 0.12 145)' },
    { name: 'Photos', value: evidenceRecords.filter(e => e.type === 'photo').length, color: 'oklch(0.55 0.16 150)' },
    { name: 'Datasets', value: evidenceRecords.filter(e => e.type === 'dataset').length, color: 'oklch(0.65 0.15 150)' },
    { name: 'Reports', value: evidenceRecords.filter(e => e.type === 'report').length, color: 'oklch(0.87 0.15 85)' },
    { name: 'Surveys', value: evidenceRecords.filter(e => e.type === 'survey').length, color: 'oklch(0.7 0.17 65)' },
  ]

  const filtered = evidenceRecords.filter((e) => {
    const matchSearch = e.title.toLowerCase().includes(search.toLowerCase()) || e.uploadedBy.toLowerCase().includes(search.toLowerCase())
    const matchType = typeFilter === 'all' || e.type === typeFilter
    const matchVerified = verifiedFilter === 'all' || (verifiedFilter === 'verified' ? e.verified : !e.verified)
    return matchSearch && matchType && matchVerified
  })

  const handleVerify = async (record) => {
    if (!record?.id || record.verified) return
    try {
      await updateDoc(doc(db, 'indicators', record.id), {
        'evidence.verified': true,
        'evidence.verifiedAt': serverTimestamp(),
        updatedAt: serverTimestamp(),
      })
      setEvidenceRecords((prev) =>
        prev.map((item) => (item.id === record.id ? { ...item, verified: true } : item)),
      )
      toast.success('Evidence verified.')
    } catch (error) {
      toast.error('Failed to verify evidence', {
        description: error?.message || 'Please try again.',
      })
    }
  }

  return (
    <div className="flex flex-col gap-6 p-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-foreground font-serif tracking-tight">Evidence Repository</h1>
          <p className="text-sm text-muted-foreground mt-1">Upload, manage, and verify evidence linked to project indicators</p>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <Card className="border border-border relative overflow-hidden">
          <CardContent className="p-4 text-center">
            <p className="text-3xl font-bold text-foreground">{loading ? '—' : evidenceRecords.length}</p>
            <p className="text-xs text-muted-foreground font-medium mt-1">Total Evidence</p>
            <div className="absolute bottom-0 left-0 right-0 h-1 bg-primary" />
          </CardContent>
        </Card>
        <Card className="border border-border relative overflow-hidden">
          <CardContent className="p-4 text-center">
            <p className="text-3xl font-bold text-success">{loading ? '—' : verifiedCount}</p>
            <p className="text-xs text-muted-foreground font-medium mt-1">Verified</p>
            <div className="absolute bottom-0 left-0 right-0 h-1 bg-success" />
          </CardContent>
        </Card>
        <Card className="border border-border relative overflow-hidden">
          <CardContent className="p-4 text-center">
            <p className="text-3xl font-bold text-warning">{loading ? '—' : pendingCount}</p>
            <p className="text-xs text-muted-foreground font-medium mt-1">Pending Review</p>
            <div className="absolute bottom-0 left-0 right-0 h-1 bg-warning" />
          </CardContent>
        </Card>
        <Card className="border border-border">
          <CardHeader className="pb-1 pt-3 px-4"><CardTitle className="text-xs font-semibold text-foreground">By Type</CardTitle></CardHeader>
          <CardContent className="px-4 pb-3">
            {loading ? (
              <div className="py-4 text-center text-xs text-muted-foreground">Loading...</div>
            ) : (
              <ResponsiveContainer width="100%" height={80}>
                <PieChart>
                  <Pie data={typePie} cx="50%" cy="50%" innerRadius={18} outerRadius={32} dataKey="value" strokeWidth={0}>
                    {typePie.map(e => <Cell key={e.name} fill={e.color} />)}
                  </Pie>
                  <Tooltip contentStyle={{ fontSize: 10, borderRadius: 8 }} />
                </PieChart>
              </ResponsiveContainer>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
          <Input placeholder="Search evidence..." className="pl-10 h-9" value={search} onChange={e => setSearch(e.target.value)} />
        </div>
        <Select value={typeFilter} onValueChange={setTypeFilter}>
          <SelectTrigger className="w-[130px] h-9"><SelectValue placeholder="Type" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Types</SelectItem>
            <SelectItem value="document">Document</SelectItem>
            <SelectItem value="photo">Photo</SelectItem>
            <SelectItem value="dataset">Dataset</SelectItem>
            <SelectItem value="report">Report</SelectItem>
            <SelectItem value="survey">Survey</SelectItem>
          </SelectContent>
        </Select>
        <Select value={verifiedFilter} onValueChange={setVerifiedFilter}>
          <SelectTrigger className="w-[130px] h-9"><SelectValue placeholder="Status" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All</SelectItem>
            <SelectItem value="verified">Verified</SelectItem>
            <SelectItem value="pending">Pending</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Evidence Table */}
      <Card className="border border-border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="text-xs">Evidence</TableHead>
              <TableHead className="text-xs">Indicator</TableHead>
              <TableHead className="text-xs">Uploaded By</TableHead>
              <TableHead className="text-xs">Date</TableHead>
              <TableHead className="text-xs">Size</TableHead>
              <TableHead className="text-xs">Status</TableHead>
              <TableHead className="text-xs w-[60px]">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={7} className="text-center text-sm text-muted-foreground py-6">
                  Loading evidence...
                </TableCell>
              </TableRow>
            ) : filtered.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} className="text-center text-sm text-muted-foreground py-6">
                  No evidence found.
                </TableCell>
              </TableRow>
            ) : filtered.map((ev) => {
              return (
                <TableRow key={ev.id}>
                  <TableCell>
                    <div className="flex items-center gap-2.5">
                      {typeIcons[ev.type]}
                      <div className="flex flex-col gap-0.5">
                        <span className="text-xs font-medium text-foreground">{ev.title}</span>
                        <span className="text-[10px] text-muted-foreground capitalize">{ev.type}</span>
                      </div>
                    </div>
                  </TableCell>
                  <TableCell className="text-xs text-muted-foreground max-w-[180px] truncate">{ev.indicatorName}</TableCell>
                  <TableCell className="text-xs text-muted-foreground">{ev.uploadedBy}</TableCell>
                  <TableCell className="text-xs text-muted-foreground">{formatDate(ev.uploadedAt)}</TableCell>
                  <TableCell className="text-xs text-muted-foreground">{formatFileSize(ev.fileSize)}</TableCell>
                  <TableCell>
                    {ev.verified ? (
                      <Badge className="bg-success/15 text-success text-[10px] gap-1"><CheckCircle className="size-3" />Verified</Badge>
                    ) : (
                      <Badge className="bg-warning/15 text-warning text-[10px] gap-1"><Clock className="size-3" />Pending</Badge>
                    )}
                  </TableCell>
                  <TableCell>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild><Button variant="ghost" size="icon" className="size-7"><MoreHorizontal className="size-4" /></Button></DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => ev.url && window.open(ev.url, '_blank')}>
                          <Eye className="mr-2 size-3.5" />View
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => ev.url && window.open(ev.url, '_blank')}>
                          <Download className="mr-2 size-3.5" />Download
                        </DropdownMenuItem>
                        {!ev.verified && canEdit ? (
                          <DropdownMenuItem onClick={() => handleVerify(ev)}>
                            <CheckCircle className="mr-2 size-3.5" />Verify
                          </DropdownMenuItem>
                        ) : null}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                </TableRow>
              )
            })}
          </TableBody>
        </Table>
      </Card>
    </div>
  )
}
